import { EstimatorAvatar, HtmlContent, makeStyles, Typography, useTranslation } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { getActivityColor } from '@paintscout/util'
import AddIcon from '@material-ui/icons/Add'
import LocationOnIcon from '@material-ui/icons/LocationOnOutlined'
import PersonIcon from '@material-ui/icons/PersonOutline'
import SquareFootIcon from '@material-ui/icons/SquareFootOutlined'
import classnames from 'classnames'
import React, { useState } from 'react'
import { activatable } from './QuoteDocumentHeader/activatable'
import QuoteDocumentHeaderMobileSheet from './QuoteDocumentHeader/QuoteDocumentHeaderMobileSheet'
import type { QuoteDocumentHeaderSection } from './QuoteDocumentHeader/QuoteDocumentHeaderMobileSheet'
import { useQuoteDocumentHeaderData } from './QuoteDocumentHeader/useQuoteDocumentHeaderData'
import WorkOrderNoteTray from './WorkOrderNoteTray'

/**
 * Sizes and colours are the prototype's phone work order (ENG-2080). Its #354962 value colour has no
 * token (it sits between P40 and P35), so it is hardcoded, as PaymentsTableNext does.
 */
const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    const border = `0.8px solid ${neutral.P90}`
    return {
      row: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        width: '100%',
        padding: theme.spacing(2),
        boxSizing: 'border-box',
        borderTop: border,
        backgroundColor: neutral.White,
        WebkitTapHighlightColor: 'transparent'
      },
      rowClickable: {
        cursor: 'pointer',
        '&:active': { backgroundColor: '#f9fafc' }
      },
      rowBody: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        flex: 1,
        minWidth: 0
      },
      label: {
        display: 'flex',
        alignItems: 'center',
        minHeight: 20,
        fontSize: theme.typography.pxToRem(14),
        fontWeight: 400,
        lineHeight: 'normal',
        color: neutral.P60
      },
      chevron: {
        width: 9,
        height: 17,
        flexShrink: 0,
        color: neutral.P60
      },
      value: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        width: '100%',
        minWidth: 0,
        fontSize: theme.typography.pxToRem(14),
        lineHeight: 'normal',
        color: '#354962'
      },
      valueIcon: {
        fontSize: 16,
        color: neutral.P50,
        flexShrink: 0
      },
      addressValue: {
        gap: 6,
        color: neutral.P35
      },
      ellipsis: {
        minWidth: 0,
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      // An empty row's "Add …" prompt: a 28px pill with a glyph and pale text, which darkens on press.
      prompt: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        height: 28,
        marginLeft: -8,
        padding: theme.spacing(0, 1.5, 0, 1),
        border: 'none',
        borderRadius: 10,
        background: neutral.White,
        font: 'inherit',
        fontSize: theme.typography.pxToRem(14),
        color: '#c5cedd',
        cursor: 'pointer',
        transition: 'background-color 0.15s, color 0.15s',
        '&:hover, &:active': { backgroundColor: '#f0f3f7', color: neutral.P60 }
      },
      // --- Total Hours ---
      hoursRow: {
        height: 60,
        padding: theme.spacing(0, 2),
        justifyContent: 'space-between',
        // It leads the stack, right under the summary row, which already draws the line above it.
        borderTop: border
      },
      hoursRight: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexShrink: 0
      },
      adjusted: {
        fontSize: theme.typography.pxToRem(12),
        color: neutral.P50,
        whiteSpace: 'nowrap'
      },
      hoursChip: {
        height: 28,
        padding: theme.spacing(0, 1),
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        border: 'none',
        borderRadius: 10,
        backgroundColor: '#EEEEEE',
        font: 'inherit',
        fontSize: theme.typography.pxToRem(18),
        fontWeight: 600,
        lineHeight: 'normal',
        color: '#51657E',
        whiteSpace: 'nowrap'
      },
      hoursChipClickable: {
        cursor: 'pointer',
        '&:active': { backgroundColor: '#DEDEDE' }
      },
      hoursInput: {
        width: 64,
        height: 28,
        padding: theme.spacing(0, 1),
        boxSizing: 'border-box',
        border: `1px solid ${neutral.P60}`,
        borderRadius: 10,
        outline: 'none',
        backgroundColor: '#EEEEEE',
        font: 'inherit',
        fontSize: theme.typography.pxToRem(18),
        fontWeight: 600,
        color: '#51657E',
        textAlign: 'center',
        '-webkit-appearance': 'none',
        '-moz-appearance': 'textfield',
        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
          '-webkit-appearance': 'none',
          margin: 0
        }
      },
      // --- notes ---
      noteBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        width: '100%'
      },
      noteValue: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        width: '100%'
      },
      noteText: {
        flex: 1,
        minWidth: 0,
        fontSize: theme.typography.pxToRem(14),
        lineHeight: 'normal',
        color: '#354962',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word'
      },
      noteIcon: {
        width: 16,
        height: 16,
        fontSize: 16,
        flexShrink: 0,
        color: neutral.P50
      },
      addNote: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        fontSize: theme.typography.pxToRem(13),
        color: neutral.P60
      },
      resetButton: {
        border: 'none',
        background: 'transparent',
        padding: theme.spacing(0.5, 1),
        borderRadius: 6,
        font: 'inherit',
        fontSize: theme.typography.pxToRem(13),
        color: neutral.P50,
        cursor: 'pointer',
        '&:active': { backgroundColor: '#f0f3f7' }
      },
      // --- Dimensions ---
      dimensions: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(1)
      },
      dimensionItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
      },
      dimensionDivider: {
        width: 1,
        height: 10,
        backgroundColor: '#dee3ec',
        flexShrink: 0
      },
      dimensionLabel: {
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P50
      },
      dimensionValue: {
        fontSize: theme.typography.pxToRem(14),
        fontWeight: 500,
        color: neutral.P35
      }
    }
  },
  { name: 'WorkOrderSummaryMobile' }
)

/** The prototype's 9×17 chevron-right: the row opens a sheet. */
function Chevron({ className }: { className?: string }) {
  return (
    <svg className={className} width="9" height="17" viewBox="0 0 9 17" fill="none" aria-hidden="true">
      <path
        d="M6.81693 8.59656C7.06102 8.8197 7.06102 9.18208 6.81693 9.40522L3.06766 12.8326C2.82357 13.0558 2.42716 13.0558 2.18307 12.8326C1.93898 12.6095 1.93898 12.2471 2.18307 12.024L5.49102 9L2.18502 5.97601C1.94093 5.75287 1.94093 5.39049 2.18502 5.16735C2.42912 4.94422 2.82552 4.94422 3.06962 5.16735L6.81888 8.59478L6.81693 8.59656Z"
        fill="currentColor"
      />
    </svg>
  )
}

export type WorkOrderNoteKey = 'crew' | 'products' | 'additionalProducts'

export interface WorkOrderMobileNote {
  key: WorkOrderNoteKey
  label: string
  /** The empty-state prompt, e.g. "Add Crew Note". Omit for a note that only shows once it has content. */
  emptyLabel?: string
  icon: React.ComponentType<{ className?: string }>
  /** Translated, display-ready HTML. */
  html: string
  /** The raw saved HTML the tray edits. */
  content: string
  hasContent: boolean
  editorRef: React.MutableRefObject<any>
  /** Shown under the label — the approved package above the product description. */
  extra?: React.ReactNode
  /** Revert an overridden description to its generated default. Only set while it is overridden. */
  onReset?: () => void
}

export interface WorkOrderSummaryMobileProps {
  hours: {
    show: boolean
    value: number
    adjusted: boolean
    editable: boolean
    editing: boolean
    draft: string
    onDraftChange: (value: string) => void
    onOpen: () => void
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => void
    onCancel: () => void
  }
  notes: WorkOrderMobileNote[]
  noteEditable: boolean
  openNote: WorkOrderNoteKey | null
  onOpenNote: (key: WorkOrderNoteKey) => void
  onCloseNote: (key: WorkOrderNoteKey) => void
  dimensions: { label: string; value: number }[]
  /** The per-quote "hide the client's email & phone" switch, offered from the Contact sheet. */
  contactDetailsToggle?: { hidden: boolean; onChange: (hidden: boolean) => void }
}

/**
 * The phone work-order summary (ENG-2080): one stack of rows under the summary row — Total Hours, Crew
 * Note, Products, Contact, Job Address, Sales Rep, Dimensions — as the prototype lays it out.
 *
 * - A note opens {@link WorkOrderNoteTray} rather than an inline editor with a toolbar.
 * - Contact, Job Address and Sales Rep show one line each, with a chevron: the row opens the same
 *   section sheet the estimate header uses, which holds every detail and the edit actions (the Sales
 *   Rep's name there is a dropdown for changing the rep in one pick).
 * - A row with no value shows its "Add …" prompt and goes straight to that section's picker.
 *
 * The save logic stays in {@link WorkOrderSummaryNext}, which the desktop layout shares.
 */
export default function WorkOrderSummaryMobile({
  hours,
  notes,
  noteEditable,
  openNote,
  onOpenNote,
  onCloseNote,
  dimensions,
  contactDetailsToggle
}: WorkOrderSummaryMobileProps) {
  const classes = useStyles()
  const { __ } = useTranslation()
  const data = useQuoteDocumentHeaderData({ variant: 'work-order' })
  const [sheetSection, setSheetSection] = useState<QuoteDocumentHeaderSection | null>(null)
  const {
    contactName,
    hasContact,
    contactClickable,
    onSelectContact,
    jobIdentifier,
    jobAddressLines,
    jobAddressClickable,
    hasJobAddress,
    onEditJobAddress,
    owner,
    ownerUser,
    userPictures,
    estimatorName,
    estimatorClickable,
    hasEstimator,
    onSelectEstimator
  } = data

  // One Contact / Job Address / Sales Rep row. With a value it opens its sheet and carries a chevron;
  // without one it shows the "Add …" prompt (when the viewer can add one) or an em dash.
  const renderSectionRow = (args: {
    label: string
    value: React.ReactNode | null
    section: QuoteDocumentHeaderSection
    prompt: { glyph: React.ReactNode; label: string; onClick?: () => void }
  }) => {
    const { label, value, section, prompt } = args
    const opensSheet = !!value
    return (
      <div
        className={classnames(classes.row, { [classes.rowClickable]: opensSheet })}
        {...activatable(opensSheet, () => setSheetSection(section))}
      >
        <div className={classes.rowBody}>
          <Typography component="span" className={classes.label}>
            {label}
          </Typography>
          {value ??
            (prompt.onClick ? (
              <button type="button" className={classes.prompt} onClick={prompt.onClick}>
                {prompt.glyph}
                {prompt.label}
              </button>
            ) : (
              <span className={classes.value}>—</span>
            ))}
        </div>
        {opensSheet && <Chevron className={classes.chevron} />}
      </div>
    )
  }

  const hoursLabel = Number.isInteger(hours.value) ? String(hours.value) : hours.value.toFixed(1)
  const jobAddressValue = jobIdentifier || jobAddressLines.join(', ')
  const estimatorAvatar = (
    <EstimatorAvatar
      user={owner ? { id: owner.id, firstName: owner.firstName, lastName: owner.lastName } : undefined}
      userPictures={userPictures}
      width="16px"
      height="16px"
      renderEmpty="placeholder"
      hideTooltip
      avatarColor={getActivityColor(ownerUser?.user_metadata?.color)}
    />
  )

  return (
    <div>
      {hours.show && (
        <div className={classnames(classes.row, classes.hoursRow)}>
          <Typography component="span" className={classes.label}>
            {__('Total Hours')}
          </Typography>
          <div className={classes.hoursRight}>
            {hours.adjusted && !hours.editing && (
              <Typography component="span" className={classes.adjusted}>
                {__('Adjusted')}
              </Typography>
            )}
            {hours.editing ? (
              <input
                className={classes.hoursInput}
                type="text"
                inputMode="decimal"
                enterKeyHint="done"
                autoFocus
                value={hours.draft}
                aria-label={__('Total Hours')}
                onChange={(ev) => hours.onDraftChange(ev.target.value)}
                onFocus={(ev) => ev.target.select()}
                onBlur={hours.onBlur}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter') {
                    ev.preventDefault()
                    ev.currentTarget.blur()
                  } else if (ev.key === 'Escape') {
                    ev.preventDefault()
                    hours.onCancel()
                  }
                }}
              />
            ) : (
              <button
                type="button"
                className={classnames(classes.hoursChip, { [classes.hoursChipClickable]: hours.editable })}
                disabled={!hours.editable}
                onClick={hours.onOpen}
              >
                {hoursLabel}
              </button>
            )}
          </div>
        </div>
      )}

      {notes.map((note) => {
        const { key, label, emptyLabel, icon: Icon, html, hasContent, extra } = note
        if (!hasContent && !emptyLabel) return null
        return (
          <div
            key={key}
            className={classnames(classes.row, { [classes.rowClickable]: noteEditable })}
            {...activatable(noteEditable, () => onOpenNote(key))}
          >
            {hasContent || !noteEditable ? (
              <div className={classes.noteBody}>
                <Typography component="span" className={classes.label}>
                  {label}
                </Typography>
                {extra}
                <div className={classes.noteValue}>
                  <Icon className={classes.noteIcon} />
                  <Typography component="div" className={classes.noteText}>
                    {hasContent ? <HtmlContent content={html} /> : '—'}
                  </Typography>
                </div>
              </div>
            ) : (
              <div className={classes.noteBody}>
                {extra}
                <span className={classes.addNote}>
                  <AddIcon className={classes.noteIcon} />
                  {emptyLabel}
                </span>
              </div>
            )}
          </div>
        )
      })}

      {renderSectionRow({
        label: __('Contact'),
        section: 'contact',
        value: hasContact ? (
          <span className={classes.value}>
            <PersonIcon className={classes.valueIcon} />
            <span className={classes.ellipsis}>{contactName || '—'}</span>
          </span>
        ) : null,
        prompt: {
          glyph: <PersonIcon className={classes.valueIcon} />,
          label: __('Add Contact'),
          onClick: contactClickable && onSelectContact ? () => onSelectContact() : undefined
        }
      })}

      {renderSectionRow({
        label: __('Job Address'),
        section: 'job',
        value: hasJobAddress ? (
          <span className={classnames(classes.value, classes.addressValue)}>
            <LocationOnIcon className={classes.valueIcon} />
            <span className={classes.ellipsis}>{jobAddressValue}</span>
          </span>
        ) : null,
        prompt: {
          glyph: <LocationOnIcon className={classes.valueIcon} />,
          label: __('Add Address'),
          onClick: jobAddressClickable ? () => onEditJobAddress() : undefined
        }
      })}

      {renderSectionRow({
        label: __('Sales Rep'),
        section: 'estimator',
        value: hasEstimator ? (
          <span className={classes.value}>
            {estimatorAvatar}
            <span className={classes.ellipsis}>{estimatorName}</span>
          </span>
        ) : null,
        prompt: {
          glyph: estimatorAvatar,
          label: __('Add Estimator'),
          onClick: estimatorClickable ? () => onSelectEstimator() : undefined
        }
      })}

      {dimensions.length > 0 && (
        <div className={classes.row}>
          <div className={classes.rowBody}>
            <Typography component="span" className={classes.label}>
              {__('Dimensions')}
            </Typography>
            <div className={classes.dimensions}>
              <SquareFootIcon className={classes.valueIcon} />
              {dimensions.map((d, index) => (
                <React.Fragment key={d.label}>
                  {index > 0 && <span className={classes.dimensionDivider} />}
                  <span className={classes.dimensionItem}>
                    <span className={classes.dimensionLabel}>{d.label}</span>
                    <span className={classes.dimensionValue}>{d.value.toLocaleString()}</span>
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {notes.map((note) => (
        <WorkOrderNoteTray
          key={note.key}
          open={openNote === note.key}
          title={note.label}
          icon={note.icon}
          content={note.content}
          placeholder={note.key === 'crew' ? __('Add a note for the crew') : undefined}
          editorRef={note.editorRef}
          onClose={() => onCloseNote(note.key)}
          headerAction={
            note.onReset ? (
              <button type="button" className={classes.resetButton} onClick={note.onReset}>
                {__('Reset')}
              </button>
            ) : undefined
          }
        />
      ))}

      <QuoteDocumentHeaderMobileSheet
        section={sheetSection}
        onClose={() => setSheetSection(null)}
        data={data}
        contactDetailsToggle={contactDetailsToggle}
      />
    </div>
  )
}
